import React from 'react';
import { C, S } from '../../utils/styles';
import { fmt } from '../../utils/formatters';
import Icon from '../Icon';
import { IBOS_STATUSES } from '../../data/ibosDefaults';

export default function IBOSLayerTable({ layers, onToggle, onSelectLayer, expandedLayerId, saving }) {
  const totalEmpr = layers.reduce((s, l) => s + (l.effectiveEnabled ? l.result.employerSavings : 0), 0);
  const totalEmp = layers.reduce((s, l) => s + (l.effectiveEnabled ? l.result.employeeValue : 0), 0);

  return (
    <div data-testid="ibos-layer-table" style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ ...S.heading, margin: 0 }}>Optimization by Layer</h3>
        {saving && <span style={{ fontSize: 10, color: C.textLight }}>Saving…</span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 36 }}>#</th>
              <th style={S.th}>Component</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Empr. Save/Yr</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Emp. Value/Yr</th>
              <th style={{ ...S.th, textAlign: 'center' }}>Status</th>
              <th style={{ ...S.th, textAlign: 'center', width: 80 }}>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {layers.map((layer, i) => {
              const statusInfo = IBOS_STATUSES[layer.effectiveStatus] || IBOS_STATUSES.na;
              const statusColor = C[statusInfo.color] || C.textLight;
              const isExpanded = expandedLayerId === layer.id;
              const isToggleable = layer.effectiveStatus === 'active' || layer.effectiveStatus === 'available';
              return (
                <tr
                  key={layer.id}
                  data-testid={`ibos-layer-row-${layer.id}`}
                  onClick={() => onSelectLayer?.(layer.id)}
                  style={{
                    background: isExpanded ? C.accentBg : i % 2 === 0 ? 'transparent' : C.bg,
                    cursor: 'pointer',
                    opacity: layer.effectiveEnabled ? 1 : 0.55,
                  }}
                >
                  <td style={{ ...S.td, ...S.mono, fontWeight: 700, color: C.textLight }}>{layer.order || i + 1}</td>
                  <td style={{ ...S.td, fontWeight: 600, color: C.text }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Icon name={layer.icon || 'cube-outline'} style={{ fontSize: 16, color: statusColor }} />
                      {layer.name}
                    </div>
                  </td>
                  <td style={{ ...S.td, ...S.mono, textAlign: 'right', fontWeight: 700, color: layer.result.employerSavings > 0 ? C.blue : C.textLight }}>
                    {layer.result.employerSavings > 0 ? fmt(layer.result.employerSavings) : '—'}
                  </td>
                  <td style={{ ...S.td, ...S.mono, textAlign: 'right', fontWeight: 700, color: layer.result.employeeValue > 0 ? C.green : C.textLight }}>
                    {layer.result.employeeValue > 0 ? fmt(layer.result.employeeValue) : '—'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 6,
                      background: `${statusColor}20`,
                      color: statusColor,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                    }}>
                      <Icon name={statusInfo.icon} style={{ fontSize: 12 }} />
                      {statusInfo.label}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid={`ibos-toggle-${layer.id}`}
                      disabled={!isToggleable}
                      onClick={() => isToggleable && onToggle?.(layer.id, !layer.effectiveEnabled)}
                      style={{
                        width: 38,
                        height: 22,
                        borderRadius: 11,
                        border: 'none',
                        background: layer.effectiveEnabled ? C.accent : '#d1d5db',
                        position: 'relative',
                        cursor: isToggleable ? 'pointer' : 'not-allowed',
                        opacity: isToggleable ? 1 : 0.5,
                        transition: 'background 0.2s',
                        padding: 0,
                      }}
                      title={isToggleable ? (layer.effectiveEnabled ? 'Turn off' : 'Turn on') : 'Layer not available for this employer'}
                    >
                      <div style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: '#fff',
                        position: 'absolute',
                        top: 3,
                        left: layer.effectiveEnabled ? 19 : 3,
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: C.bg }}>
              <td style={{ ...S.td, fontWeight: 800 }} colSpan={2}>TOTAL (enabled layers)</td>
              <td style={{ ...S.td, ...S.mono, textAlign: 'right', fontWeight: 800, color: C.blue }} data-testid="ibos-total-employer">{fmt(totalEmpr)}</td>
              <td style={{ ...S.td, ...S.mono, textAlign: 'right', fontWeight: 800, color: C.green }} data-testid="ibos-total-employee">{fmt(totalEmp)}</td>
              <td style={S.td} colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ padding: '10px 20px', borderTop: `1px solid ${C.border}`, fontSize: 10, color: C.textLight, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {Object.entries(IBOS_STATUSES).map(([key, info]) => (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon name={info.icon} style={{ fontSize: 12, color: C[info.color] || C.textLight }} />
            {info.label}: {info.description}
          </span>
        ))}
      </div>
    </div>
  );
}
